import comp from '../components/barrel';

const LAYOUT_EP = 'dashboard/get-layout';
const GRAPHS_EP = 'dashboard/get-allocated-graphs';
const PUBLISH_EP = 'dashboard/publish';
const RENDER_EP = 'dashboard/render-graph';

function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function emptySlots() {
  return [1, 2, 3, 4, 5, 6].map((slot_no) => ({
    slot_no,
    graph_uuid: null,
    short_text: null,
    graph_kind: null,
    filters: {},
  }));
}

function payloadData(result) {
  return result && result.data !== undefined ? result.data : null;
}

export default {
  async prepare() {
    this.tab = 'focussed';
    this.slots = emptySlots();
    this.graphs = [];
    this.graphsLoaded = false;
    this.recipe.methods.push(await O.mPOST(LAYOUT_EP, { tab: 'focussed' }));
  },

  async render() {
    const rec = this.recipe.methods;
    let data = {};
    if (rec.length) data = await O.PREP(rec);
    this.slots = this.normalizeSlots(payloadData(data[LAYOUT_EP]));
    this.bindPage();
    this.paintEdit();
    await this.paintSlots();
  },

  normalizeSlots(rows) {
    const slots = emptySlots();
    if (!Array.isArray(rows)) return slots;
    rows.forEach((row) => {
      const index = Number(row.slot_no) - 1;
      if (index < 0 || index > 5) return;
      slots[index] = {
        slot_no: Number(row.slot_no),
        graph_uuid: row.graph_uuid || null,
        short_text: row.short_text || null,
        graph_kind: row.graph_kind || null,
        filters: row.filters && typeof row.filters === 'object' ? row.filters : {},
      };
    });
    return slots;
  },

  graphById(graph_uuid) {
    return this.graphs.find((graph) => graph.graph_uuid === graph_uuid) || null;
  },

  bindPage() {
    const focussed = U.id('dashboardTabFocussed');
    const other = U.id('dashboardTabOther');
    const editBtn = U.id('editDashboardBtn');
    const graphSelect = U.id('dashboardGraph');
    const publishBtn = U.id('publishDashboardBtn');

    if (focussed) focussed.onclick = () => this.changeTab('focussed');
    if (other) other.onclick = () => this.changeTab('other');
    if (editBtn) editBtn.onclick = () => this.toggleEdit();
    if (graphSelect) {
      graphSelect.onchange = () => {
        const slot = this.slots[Number(U.id('dashboardSlot').value) - 1];
        const saved = slot && slot.graph_uuid === graphSelect.value ? slot.filters : {};
        this.paintFilters(saved);
      };
    }
    if (publishBtn) publishBtn.onclick = () => this.publish();
    this.markTab();
  },

  markTab() {
    const focussed = U.id('dashboardTabFocussed');
    const other = U.id('dashboardTabOther');
    if (focussed) focussed.className = this.tab === 'focussed' ? 'btn' : 'btn dashboard-tab-idle';
    if (other) other.className = this.tab === 'other' ? 'btn' : 'btn dashboard-tab-idle';
  },

  async toggleEdit() {
    const panel = U.id('dashboardEdit');
    if (!panel) return;
    if (!panel.hidden) {
      panel.hidden = true;
      const editBtn = U.id('editDashboardBtn');
      if (editBtn) editBtn.hidden = false;
      return;
    }
    if (!this.graphsLoaded) {
      try {
        await this.loadGraphs();
      } catch (error) {
        comp.toast('Unable to load graphs');
        return;
      }
    }
    panel.hidden = false;
    const editBtn = U.id('editDashboardBtn');
    if (editBtn) editBtn.hidden = true;
  },

  async loadGraphs() {
    const graphSelect = U.id('dashboardGraph');
    const selected = graphSelect ? graphSelect.value : '';
    const res = await O.PREP([await O.mPOST(GRAPHS_EP, {})]);
    const graphs = payloadData(res[GRAPHS_EP]);
    this.graphs = Array.isArray(graphs) ? graphs : [];
    this.graphsLoaded = true;
    this.paintEdit();
    if (graphSelect && this.graphs.some((graph) => graph.graph_uuid === selected)) {
      graphSelect.value = selected;
    }
  },

  paintEdit() {
    const graphSelect = U.id('dashboardGraph');
    if (!graphSelect) return;
    const options = ['<option value="">Choose/remove</option>'].concat(
      this.graphs.map(
        (graph) => `<option value="${esc(graph.graph_uuid)}">${esc(graph.short_text)}</option>`
      )
    );
    graphSelect.innerHTML = options.join('');
  },

  paintFilters(saved) {
    const host = U.id('dashboardFilters');
    const graphSelect = U.id('dashboardGraph');
    if (!host || !graphSelect) return;
    const graph = this.graphById(graphSelect.value);
    const schema = graph && Array.isArray(graph.filter_schema) ? graph.filter_schema : [];
    const filters = saved && typeof saved === 'object' ? saved : {};
    if (!schema.length) {
      host.innerHTML = '';
      return;
    }
    host.innerHTML = schema
      .map((field) => {
        const value = filters[field.key] != null ? filters[field.key] : '';
        return `<div class="input-group"><label for="dashFilter_${esc(field.key)}">${esc(field.label || field.key)}</label>${this.filterControl(field, value)}</div>`;
      })
      .join('');
  },

  filterControl(field, value) {
    const id = `dashFilter_${esc(field.key)}`;
    if (field.type === 'select') {
      const options = (field.options || [])
        .map(
          (option) =>
            `<option value="${esc(option.value)}"${String(option.value) === String(value) ? ' selected' : ''}>${esc(option.label || option.value)}</option>`
        )
        .join('');
      return `<select id="${id}" data-filter-key="${esc(field.key)}"><option value="">Select</option>${options}</select>`;
    }
    const type = field.type === 'date' || field.type === 'number' ? field.type : 'text';
    return `<input id="${id}" data-filter-key="${esc(field.key)}" type="${type}" value="${esc(value)}" />`;
  },

  readFilters() {
    const host = U.id('dashboardFilters');
    const filters = {};
    if (!host) return filters;
    host.querySelectorAll('[data-filter-key]').forEach((input) => {
      filters[input.getAttribute('data-filter-key')] = input.value;
    });
    return filters;
  },

  async changeTab(tab) {
    if (this.tab === tab) return;
    this.tab = tab;
    this.markTab();
    try {
      const res = await O.PREP([await O.mPOST(LAYOUT_EP, { tab })]);
      this.slots = this.normalizeSlots(payloadData(res[LAYOUT_EP]));
      await this.paintSlots();
    } catch (error) {
      comp.toast('Unable to load dashboard');
    }
  },

  async paintSlots() {
    const grid = U.id('dashboardGrid');
    if (!grid) return;
    grid.innerHTML = this.slots
      .map(
        (slot) => `
        <section class="dashboard-block" id="dashboardBlock${slot.slot_no}">
          <div class="dashboard-slot-head">
            <h4>${esc(slot.short_text || `Slot ${slot.slot_no}`)}</h4>
            <span class="dashboard-info" hidden>
              <button type="button" class="dashboard-info-btn" aria-label="Graph information">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                  <circle cx="12" cy="12" r="9"></circle>
                  <path stroke-linecap="round" d="M12 11v5"></path>
                  <circle cx="12" cy="8" r="0.9" fill="currentColor" stroke="none"></circle>
                </svg>
              </button>
              <span class="dashboard-info-tip" role="tooltip"></span>
            </span>
          </div>
          <div class="dashboard-slot-body">${slot.graph_uuid ? 'Loading' : ''}</div>
        </section>`
      )
      .join('');

    await Promise.all(
      this.slots.filter((slot) => slot.graph_uuid).map((slot) => this.renderSlot(slot))
    );
  },

  async renderSlot(slot) {
    const block = U.id(`dashboardBlock${slot.slot_no}`);
    const body = block ? block.querySelector('.dashboard-slot-body') : null;
    if (!body) return;
    try {
      const res = await O.PREP([
        await O.mPOST(RENDER_EP, { graph_uuid: slot.graph_uuid, filters: slot.filters || {} }),
      ]);
      const data = payloadData(res[RENDER_EP]);
      if (!data) {
        body.textContent = 'Unable to load graph';
        return;
      }
      const title = block.querySelector('h4');
      if (title && data.short_text) title.textContent = data.short_text;
      const info = block.querySelector('.dashboard-info');
      const tip = block.querySelector('.dashboard-info-tip');
      const longText = data.long_text ? String(data.long_text).trim() : '';
      if (info && tip) {
        tip.textContent = longText;
        info.hidden = !longText;
      }
      if (data.message) {
        body.textContent = data.message;
        return;
      }
      if (data.kind === 'chart' && typeof data.svg === 'string' && data.svg.trim().startsWith('<svg')) {
        body.innerHTML = data.svg;
        return;
      }
      if (data.kind === 'list') {
        body.innerHTML = this.listTable(data.columns, data.rows);
        return;
      }
      body.textContent = 'Unable to load graph';
    } catch (error) {
      body.textContent = 'Unable to load graph';
    }
  },

  listTable(columns, rows) {
    const cols = Array.isArray(columns) ? columns : [];
    const bodyRows = Array.isArray(rows) ? rows : [];
    const head = cols.map((col) => `<th>${esc(col.label || col.field)}</th>`).join('');
    const body = bodyRows
      .map((row) => {
        const cells = cols.map((col) => `<td>${esc(row[col.field])}</td>`).join('');
        return `<tr>${cells}</tr>`;
      })
      .join('');
    return `<table><thead><tr>${head}</tr></thead><tbody>${body || '<tr><td>No rows</td></tr>'}</tbody></table>`;
  },

  async publish() {
    const slotSelect = U.id('dashboardSlot');
    const graphSelect = U.id('dashboardGraph');
    if (!slotSelect || !graphSelect) return;

    const slot_no = Number(slotSelect.value);
    const graph = this.graphById(graphSelect.value);
    const graph_uuid = graph ? graph.graph_uuid : null;

    this.slots[slot_no - 1] = {
      slot_no,
      graph_uuid,
      short_text: graph ? graph.short_text : null,
      graph_kind: graph ? graph.graph_kind : null,
      filters: {},
    };

    try {
      const res = await O.PREP([
        await O.mPOST(PUBLISH_EP, {
          tab: this.tab,
          slot_no,
          graph_uuid,
        }),
      ]);
      const saved = payloadData(res[PUBLISH_EP]);
      if (saved && saved.slot_no) {
        this.slots[saved.slot_no - 1].graph_uuid = saved.graph_uuid || null;
      }
      comp.toast('Dashboard published');
      try {
        await this.loadGraphs();
      } catch (error) {
        comp.toast('Unable to refresh graphs');
      }
      const panel = U.id('dashboardEdit');
      const editBtn = U.id('editDashboardBtn');
      if (panel) panel.hidden = true;
      if (editBtn) editBtn.hidden = false;
      await this.paintSlots();
    } catch (error) {
      comp.toast('Unable to publish dashboard');
    }
  },
};
